import type { DrydockExecutionState, DrydockJobEvent } from '@felix/domain';
import {
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Textarea,
  TextInput,
} from '@mantine/core';
import {
  IconArrowBack,
  IconArrowRight,
  IconCheck,
  IconLink,
  IconLockOpen,
  IconPlayerPlay,
  IconSearch,
  IconTool,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReauthField, useReauth } from '../../approval-policy.js';
import { formatDateTime } from '../../format.js';
import { Panel } from '../../Panel.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import classes from './Drydock.module.css';
import { useDrydockErrorText } from './errors.js';
import type { DrydockScopeSource } from './source.js';
import type {
  DrydockJobEventInput,
  DrydockJobRow,
  DrydockLinkCandidate,
  DrydockLinkTargetType,
  DrydockScope,
  DrydockTargetLink,
  PdiFindingRow,
} from './types.js';

/**
 * Tersane takibi ve doğrulama panelleri (ADR 0129 Ek Faz 4; Excel Screen 10 "Execution &
 * Verification", Screen 11 "Close Finding"). Gemide ilerleme (başladı, yüzde, tamamlandı,
 * yapılamadı, kapandı) ve bulgu kapanışı yazılır; ofiste Superintendent tamamlanan işi ve kritik
 * bulgu kapanışını doğrular ya da iade eder. Düğmeler `scope.can`, kaynağın işlemleri ve proje
 * durumuyla görünür; sunucu her yazmada yine denetler.
 */

export const EXECUTION_TONE: Record<DrydockExecutionState, StatusTone> = {
  not_started: 'neutral',
  in_progress: 'info',
  completed: 'warning',
  verified: 'success',
  closed: 'success',
  pending: 'danger',
};

const text = (value: string) => value.trim() || null;

/** İşin icra rozeti (yüzde ve "ofiste bekliyor" ile). */
export function ExecutionBadge({ job }: { job: DrydockJobRow }) {
  const { t } = useTranslation();
  const execution = job.execution;
  if (!execution) return <span>—</span>;
  const percent =
    execution.state === 'in_progress' && execution.percent !== null ? ` ${execution.percent}%` : '';
  return (
    <Group gap={4} wrap="nowrap">
      <StatusBadge tone={EXECUTION_TONE[execution.state]}>
        {t(`drydock.yard.executionStates.${execution.state}`)}
        {percent}
      </StatusBadge>
      {execution.awaitingOffice ? (
        <StatusBadge tone="warning">{t('drydock.yard.awaitingOffice')}</StatusBadge>
      ) : null}
    </Group>
  );
}

/** Ofis doğrulamasının rozeti (doğrulama sütunu). */
export function VerificationBadge({ job }: { job: DrydockJobRow }) {
  const { t } = useTranslation();
  const execution = job.execution;
  if (!execution) return <span>—</span>;
  if (execution.verification === 'verified' || execution.state === 'closed') {
    return <StatusBadge tone="success">{t('drydock.yard.verdicts.verified')}</StatusBadge>;
  }
  if (execution.state === 'completed') {
    return <StatusBadge tone="warning">{t('drydock.yard.verdicts.waiting')}</StatusBadge>;
  }
  if (execution.verifications.at(-1)?.verdict === 'returned' && execution.state === 'in_progress') {
    return <StatusBadge tone="danger">{t('drydock.yard.verdicts.returned')}</StatusBadge>;
  }
  return <span>—</span>;
}

/** Olay düğmeleri: icra durumuna göre (domain `ALLOWED` ile aynı). */
export function eventChoices(state: DrydockExecutionState): DrydockJobEvent[] {
  switch (state) {
    case 'not_started':
      return ['started', 'not_done'];
    case 'pending':
      return ['started'];
    case 'in_progress':
      return ['progress', 'completed', 'not_done'];
    case 'verified':
      return ['closed'];
    default:
      return [];
  }
}

const EVENT_ACTION: Partial<Record<DrydockJobEvent, string>> = {
  started: 'start',
  progress: 'progress',
  completed: 'complete',
  not_done: 'notDone',
  closed: 'close',
};

function useSave<T>(save: () => Promise<T>, onSaved: (value: T) => void) {
  const errorText = useDrydockErrorText();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function run() {
    setBusy(true);
    setError(null);
    try {
      onSaved(await save());
    } catch (err) {
      setError(errorText(err));
      return err;
    } finally {
      setBusy(false);
    }
    return null;
  }
  return { busy, error, run };
}

/** Bağlı kayıtların listesi (iş emri, talep, RA doğrulaması, sörvey). */
function TargetLinks({ links }: { links: readonly DrydockTargetLink[] }) {
  const { t } = useTranslation();
  if (links.length === 0) return <p className={classes.muted}>{t('drydock.yard.noLinks')}</p>;
  return (
    <ul className={classes.inspections}>
      {links.map((l) => (
        <li key={l.linkId}>
          <span className={classes.muted}>{t(`drydock.yard.linkTypes.${l.type}`)}</span>
          <span className={classes.mono}>{l.label}</span>
          {l.status ? <StatusBadge tone="neutral">{l.status}</StatusBadge> : null}
          <span className={classes.muted}>{t(`drydock.scope.relations.${l.relation}`)}</span>
        </li>
      ))}
    </ul>
  );
}

type JobForm =
  | null
  | { event: DrydockJobEvent }
  | { verify: 'verified' | 'returned' }
  | 'link'
  | 'carry';

/** İş çekmecesindeki icra, doğrulama, bağ ve taşıma bölümü. */
export function JobExecutionPanel({
  job,
  scope,
  source,
  onSaved,
}: {
  job: DrydockJobRow;
  scope: DrydockScope;
  source: DrydockScopeSource;
  onSaved: (scope: DrydockScope) => void;
}) {
  const { t } = useTranslation();
  const errorText = useDrydockErrorText();
  const [form, setForm] = useState<JobForm>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const execution = job.execution;
  if (!execution) return null;
  const can = scope.can;
  const ship = scope.side === 'ship' && job.state === 'open';
  const inYard = scope.project.state === 'in_yard';
  const projectOpen = scope.project.state === 'planning' || inYard;
  const approved = job.status === 'approved';
  const provisionalStart = Boolean(job.additional) && job.status === 'for_review';
  // Olay düğmesi: ilerleme `edit`; onaysız ek işi başlatma ve kapanış `approve` (Master, Chief)
  const allowed = (event: DrydockJobEvent) => {
    if (!ship || !inYard || !source.jobEvent) return false;
    if (event === 'closed') return can.review && approved;
    if (event === 'started' && !approved) return provisionalStart && can.review;
    // Onaysız ek iş başlatıldıysa (provisional) ilerlemesi de girilir
    return (approved || execution.provisional) && can.edit;
  };
  const events = eventChoices(execution.state).filter(allowed);
  const verifiable =
    scope.side === 'office' && execution.state === 'completed' && Boolean(source.verifyJob);
  const saved = (next: DrydockScope) => {
    setForm(null);
    onSaved(next);
  };

  async function openWorkOrder() {
    if (!source.openWorkOrder) return;
    setBusy(true);
    setError(null);
    try {
      onSaved(await source.openWorkOrder({ jobId: job.id }));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Panel title={t('drydock.yard.execution')}>
        <div className={classes.detail}>
          <Group gap="xs">
            <ExecutionBadge job={job} />
            {job.additional ? (
              <StatusBadge tone="info">{t('drydock.yard.additional')}</StatusBadge>
            ) : null}
            {execution.provisional ? (
              <StatusBadge tone="warning">{t('drydock.yard.provisional')}</StatusBadge>
            ) : null}
          </Group>
          {job.carriedFrom ? (
            <p className={classes.muted}>
              {t('drydock.yard.carriedFrom', {
                job: job.carriedFrom.jobNo,
                project: job.carriedFrom.projectNo,
              })}
            </p>
          ) : null}
          {job.carriedTo ? (
            <p className={classes.muted}>
              {t('drydock.yard.carriedTo', {
                job: job.carriedTo.jobNo,
                project: job.carriedTo.projectNo,
              })}
            </p>
          ) : null}
          {error ? <Alert color="red">{error}</Alert> : null}
          <Group justify="flex-end" gap="xs">
            {events.map((event) => (
              <Button
                key={event}
                variant={event === 'completed' || event === 'closed' ? 'filled' : 'default'}
                leftSection={
                  event === 'started' ? (
                    <IconPlayerPlay size={16} aria-hidden />
                  ) : event === 'closed' || event === 'completed' ? (
                    <IconCheck size={16} aria-hidden />
                  ) : undefined
                }
                onClick={() => setForm({ event })}
              >
                {t(`drydock.yard.actions.${EVENT_ACTION[event]}`)}
              </Button>
            ))}
            {verifiable && can.return ? (
              <Button
                variant="default"
                color="red"
                leftSection={<IconArrowBack size={16} aria-hidden />}
                onClick={() => setForm({ verify: 'returned' })}
              >
                {t('drydock.yard.actions.return')}
              </Button>
            ) : null}
            {verifiable && can.verify ? (
              <Button
                leftSection={<IconCheck size={16} aria-hidden />}
                onClick={() => setForm({ verify: 'verified' })}
              >
                {t('drydock.yard.actions.verify')}
              </Button>
            ) : null}
            {ship &&
            execution.state === 'pending' &&
            !job.carriedTo &&
            can.create &&
            source.carryJob &&
            source.carryTargets ? (
              <Button
                variant="default"
                leftSection={<IconArrowRight size={16} aria-hidden />}
                onClick={() => setForm('carry')}
              >
                {t('drydock.yard.actions.carry')}
              </Button>
            ) : null}
          </Group>
          {execution.events.length === 0 ? (
            <p className={classes.muted}>{t('drydock.yard.noEvents')}</p>
          ) : (
            <ol className={classes.inspections}>
              {execution.events.map((e) => {
                const verdict = execution.verifications.find((v) => v.eventId === e.id);
                return (
                  <li key={e.id}>
                    <span className={classes.mono}>{formatDateTime(e.occurredAt)}</span>
                    <strong>
                      {t(`drydock.yard.events.${e.event}`)}
                      {e.percent !== null ? ` ${e.percent}%` : ''}
                    </strong>
                    <span>{e.byName ?? '—'}</span>
                    {e.safetyReview ? (
                      <span className={classes.muted}>
                        {t(`drydock.yard.safetyReviews.${e.safetyReview}`)}
                      </span>
                    ) : null}
                    {e.note ? <span className={classes.muted}>{e.note}</span> : null}
                    {verdict ? (
                      <StatusBadge tone={verdict.verdict === 'verified' ? 'success' : 'danger'}>
                        {t(`drydock.yard.verdicts.${verdict.verdict}`)} · {verdict.byName ?? '—'}
                        {verdict.note ? ` · ${verdict.note}` : ''}
                      </StatusBadge>
                    ) : null}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </Panel>
      <Panel title={t('drydock.yard.links')}>
        <TargetLinks links={job.links ?? []} />
        {ship && projectOpen && can.create ? (
          <Group justify="flex-end" gap="xs">
            {source.openWorkOrder && job.equipmentId ? (
              <Button
                variant="default"
                loading={busy}
                leftSection={<IconTool size={16} aria-hidden />}
                onClick={() => void openWorkOrder()}
              >
                {t('drydock.yard.actions.workOrder')}
              </Button>
            ) : null}
            {source.link && source.linkCandidates ? (
              <Button
                variant="default"
                leftSection={<IconLink size={16} aria-hidden />}
                onClick={() => setForm('link')}
              >
                {t('drydock.yard.actions.link')}
              </Button>
            ) : null}
          </Group>
        ) : null}
      </Panel>
      {form !== null && typeof form === 'object' && 'event' in form ? (
        <EventModal
          job={job}
          event={form.event}
          source={source}
          onClose={() => setForm(null)}
          onSaved={saved}
        />
      ) : null}
      {form !== null && typeof form === 'object' && 'verify' in form ? (
        <VerificationModal
          title={t(`drydock.yard.verificationTitle.${form.verify}`, { no: job.jobNo })}
          hint={t(`drydock.yard.verificationHint.${form.verify}`)}
          verdict={form.verify}
          onClose={() => setForm(null)}
          onSave={(input) =>
            (source.verifyJob as NonNullable<DrydockScopeSource['verifyJob']>)({
              jobId: job.id,
              verification: {
                verdict: form.verify,
                note: input.note,
                contentHash:
                  execution.events.find((e) => e.id === execution.completionId)?.contentHash ?? '',
              },
              ...input.body,
            })
          }
          onSaved={saved}
        />
      ) : null}
      {form === 'link' ? (
        <LinkModal
          title={t('drydock.yard.linkTitle', { no: job.jobNo })}
          types={['work_order', 'requisition', 'ra_verification']}
          source={source}
          onClose={() => setForm(null)}
          onLink={(candidate) =>
            (source.link as NonNullable<DrydockScopeSource['link']>)({
              projectId: scope.project.id,
              link: {
                sourceType: 'job',
                sourceId: job.id,
                targetType: candidate.type,
                targetId: candidate.id,
                relation: 'related',
              },
            })
          }
          onSaved={saved}
        />
      ) : null}
      {form === 'carry' ? (
        <CarryModal job={job} source={source} onClose={() => setForm(null)} onSaved={saved} />
      ) : null}
    </>
  );
}

/** Olay penceresi: başlatmada güvenlik incelemesi, ilerlemede yüzde, diğerlerinde not. */
function EventModal({
  job,
  event,
  source,
  onClose,
  onSaved,
}: {
  job: DrydockJobRow;
  event: DrydockJobEvent;
  source: DrydockScopeSource;
  onClose: () => void;
  onSaved: (scope: DrydockScope) => void;
}) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const [percent, setPercent] = useState<number | string>(job.execution?.percent ?? 50);
  const [safety, setSafety] = useState<'linked' | 'not_required' | null>(null);
  const input: DrydockJobEventInput = {
    event,
    note: text(note),
    ...(event === 'progress' ? { percent: Number(percent) } : {}),
    ...(event === 'started' ? { safetyReview: safety } : {}),
  };
  const { busy, error, run } = useSave(
    () =>
      (source.jobEvent as NonNullable<DrydockScopeSource['jobEvent']>)({
        jobId: job.id,
        event: input,
      }),
    onSaved,
  );
  const needsNote =
    event === 'completed' ||
    event === 'not_done' ||
    event === 'pending' ||
    (event === 'started' && safety === 'not_required');
  const ready =
    (!needsNote || note.trim() !== '') &&
    (event !== 'started' || safety !== null) &&
    (event !== 'progress' || (Number(percent) >= 1 && Number(percent) <= 99));
  return (
    <Modal
      opened
      onClose={onClose}
      title={t('drydock.yard.eventTitle', {
        event: t(`drydock.yard.events.${event}`),
        no: job.jobNo,
      })}
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
    >
      <div className={classes.form}>
        <p className={classes.muted}>{t(`drydock.yard.eventHints.${event}`)}</p>
        {error ? <Alert color="red">{error}</Alert> : null}
        {event === 'started' ? (
          <Select
            label={t('drydock.yard.safetyReview')}
            required
            data={(['linked', 'not_required'] as const).map((value) => ({
              value,
              label: t(`drydock.yard.safetyReviews.${value}`),
            }))}
            value={safety}
            onChange={(value) => setSafety(value as typeof safety)}
          />
        ) : null}
        {event === 'progress' ? (
          <NumberInput
            label={t('drydock.yard.percent')}
            required
            min={1}
            max={99}
            suffix="%"
            value={percent}
            onChange={setPercent}
          />
        ) : null}
        <Textarea
          label={t('drydock.yard.note')}
          required={needsNote}
          autosize
          minRows={2}
          maxLength={2000}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
        />
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose}>
            {t('drydock.cancel')}
          </Button>
          <Button loading={busy} disabled={!ready} onClick={() => void run()}>
            {t(`drydock.yard.actions.${EVENT_ACTION[event] ?? 'progress'}`)}
          </Button>
        </Group>
      </div>
    </Modal>
  );
}

/** Ofis doğrulaması ya da iadesi (Superintendent; iade gerekçeli; ayar açıksa parola). */
function VerificationModal({
  title,
  hint,
  verdict,
  onClose,
  onSave,
  onSaved,
}: {
  title: string;
  hint: string;
  verdict: 'verified' | 'returned';
  onClose: () => void;
  onSave: (input: { note: string | null; body: { password?: string } }) => Promise<DrydockScope>;
  onSaved: (scope: DrydockScope) => void;
}) {
  const { t } = useTranslation();
  const reauth = useReauth('drydock_verification');
  const [note, setNote] = useState('');
  const { busy, error, run } = useSave(
    () => onSave({ note: text(note), body: reauth.body() }),
    onSaved,
  );
  const needsNote = verdict === 'returned';
  return (
    <Modal
      opened
      onClose={onClose}
      title={title}
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
    >
      <div className={classes.form}>
        <p className={classes.muted}>{hint}</p>
        {error ? <Alert color="red">{error}</Alert> : null}
        <Textarea
          label={t(needsNote ? 'drydock.scope.fields.reason' : 'drydock.yard.note')}
          required={needsNote}
          autosize
          minRows={2}
          maxLength={2000}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
        />
        <ReauthField reauth={reauth} label={t('drydock.scope.password')} />
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose}>
            {t('drydock.cancel')}
          </Button>
          <Button
            {...(needsNote ? { color: 'red' } : {})}
            loading={busy}
            disabled={reauth.missing || (needsNote && note.trim() === '')}
            onClick={() =>
              void run().then((err) => {
                if (err) reauth.noteError(err);
              })
            }
          >
            {t(`drydock.yard.actions.${needsNote ? 'return' : 'verify'}`)}
          </Button>
        </Group>
      </div>
    </Modal>
  );
}

/** Bağ penceresi: türü seç, ara, kaydı bağla (bu modül hedefi değiştirmez). */
function LinkModal({
  title,
  types,
  source,
  onClose,
  onLink,
  onSaved,
}: {
  title: string;
  types: DrydockLinkTargetType[];
  source: DrydockScopeSource;
  onClose: () => void;
  onLink: (candidate: DrydockLinkCandidate) => Promise<DrydockScope>;
  onSaved: (scope: DrydockScope) => void;
}) {
  const { t } = useTranslation();
  const errorText = useDrydockErrorText();
  const [type, setType] = useState<DrydockLinkTargetType>(types[0] as DrydockLinkTargetType);
  const [q, setQ] = useState('');
  const [candidates, setCandidates] = useState<DrydockLinkCandidate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    setCandidates(null);
    (source.linkCandidates as NonNullable<DrydockScopeSource['linkCandidates']>)({
      type,
      ...(q.trim() ? { q: q.trim() } : {}),
    })
      .then((result) => {
        if (alive) setCandidates(result.candidates);
      })
      .catch((err) => {
        if (alive) setError(errorText(err));
      });
    return () => {
      alive = false;
    };
  }, [source, type, q, errorText]);

  async function link(candidate: DrydockLinkCandidate) {
    setBusy(candidate.id);
    setError(null);
    try {
      onSaved(await onLink(candidate));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Modal
      opened
      onClose={onClose}
      title={title}
      size="lg"
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
    >
      <div className={classes.form}>
        {error ? <Alert color="red">{error}</Alert> : null}
        {types.length > 1 ? (
          <Select
            label={t('drydock.yard.linkType')}
            data={types.map((value) => ({ value, label: t(`drydock.yard.linkTypes.${value}`) }))}
            value={type}
            allowDeselect={false}
            onChange={(value) => setType(value as DrydockLinkTargetType)}
          />
        ) : null}
        <TextInput
          label={t('drydock.yard.linkSearch')}
          leftSection={<IconSearch size={16} aria-hidden />}
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
        />
        {candidates === null ? null : candidates.length === 0 ? (
          <p className={classes.muted}>{t('drydock.yard.noCandidates')}</p>
        ) : (
          <ul className={classes.inspections}>
            {candidates.map((c) => (
              <li key={c.id}>
                <span className={classes.mono}>{c.label}</span>
                {c.status ? <StatusBadge tone="neutral">{c.status}</StatusBadge> : null}
                <Button
                  size="compact-sm"
                  variant="default"
                  loading={busy === c.id}
                  onClick={() => void link(c)}
                >
                  {t('drydock.yard.actions.link')}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}

/** Pending işi yeni projeye taşıma (eski kayıt değişmez). */
function CarryModal({
  job,
  source,
  onClose,
  onSaved,
}: {
  job: DrydockJobRow;
  source: DrydockScopeSource;
  onClose: () => void;
  onSaved: (scope: DrydockScope) => void;
}) {
  const { t } = useTranslation();
  const errorText = useDrydockErrorText();
  const [targets, setTargets] = useState<{ value: string; label: string }[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [project, setProject] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    (source.carryTargets as NonNullable<DrydockScopeSource['carryTargets']>)()
      .then((list) => {
        if (!alive) return;
        setTargets(
          list
            .filter((p) => p.id !== job.projectId)
            .map((p) => ({ value: p.id, label: `${p.projectNo} · ${p.title}` })),
        );
      })
      .catch((err) => {
        if (alive) setLoadError(errorText(err));
      });
    return () => {
      alive = false;
    };
  }, [source, job.projectId, errorText]);
  const { busy, error, run } = useSave(
    () =>
      (source.carryJob as NonNullable<DrydockScopeSource['carryJob']>)({
        jobId: job.id,
        toProjectId: project as string,
      }),
    onSaved,
  );
  return (
    <Modal
      opened
      onClose={onClose}
      title={t('drydock.yard.carryTitle', { no: job.jobNo })}
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
    >
      <div className={classes.form}>
        <p className={classes.muted}>{t('drydock.yard.carryHint')}</p>
        {error || loadError ? <Alert color="red">{error ?? loadError}</Alert> : null}
        <Select
          label={t('drydock.yard.carryProject')}
          required
          data={targets}
          value={project}
          onChange={setProject}
        />
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose}>
            {t('drydock.cancel')}
          </Button>
          <Button loading={busy} disabled={project === null} onClick={() => void run()}>
            {t('drydock.yard.actions.carry')}
          </Button>
        </Group>
      </div>
    </Modal>
  );
}

type FindingForm = null | 'close' | 'reopen' | 'link' | { verify: 'verified' | 'returned' };

/** Bulgu çekmecesindeki kapanış, ofis doğrulaması ve sörvey bağları bölümü. */
export function FindingClosurePanel({
  finding,
  scope,
  source,
  onSaved,
}: {
  finding: PdiFindingRow;
  scope: DrydockScope;
  source: DrydockScopeSource;
  onSaved: (scope: DrydockScope) => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<FindingForm>(null);
  const can = scope.can;
  const closure = finding.closure ?? null;
  const ship = scope.side === 'ship' && finding.state === 'open';
  const projectOpen = scope.project.state === 'planning' || scope.project.state === 'in_yard';
  const closable =
    ship &&
    can.review &&
    Boolean(source.closeFinding) &&
    (finding.status === 'reviewed' || finding.status === 'actioned');
  const reopenable = ship && projectOpen && can.void && closure !== null && source.reopenFinding;
  const verifiable =
    scope.side === 'office' &&
    closure?.critical === true &&
    closure.verdict === null &&
    Boolean(source.verifyFinding);
  const saved = (next: DrydockScope) => {
    setForm(null);
    onSaved(next);
  };
  return (
    <>
      <Panel title={t('drydock.yard.closure')}>
        <div className={classes.detail}>
          {closure ? (
            <>
              <p>
                {closure.note}{' '}
                <span className={classes.muted}>
                  {t('drydock.yard.closedBy', {
                    name: closure.closedByName ?? '—',
                    date: formatDateTime(closure.createdAt),
                  })}
                </span>
              </p>
              {closure.critical ? (
                <Group gap="xs">
                  <StatusBadge tone="warning">{t('drydock.yard.critical')}</StatusBadge>
                  <StatusBadge
                    tone={
                      closure.verdict === 'verified'
                        ? 'success'
                        : closure.verdict === 'returned'
                          ? 'danger'
                          : 'warning'
                    }
                  >
                    {t(`drydock.yard.verdicts.${closure.verdict ?? 'waiting'}`)}
                    {closure.verifierName ? ` · ${closure.verifierName}` : ''}
                  </StatusBadge>
                </Group>
              ) : null}
            </>
          ) : null}
          <Group justify="flex-end" gap="xs">
            {reopenable ? (
              <Button
                variant="default"
                leftSection={<IconLockOpen size={16} aria-hidden />}
                onClick={() => setForm('reopen')}
              >
                {t('drydock.yard.actions.reopenFinding')}
              </Button>
            ) : null}
            {closable ? (
              <Button
                leftSection={<IconCheck size={16} aria-hidden />}
                onClick={() => setForm('close')}
              >
                {t('drydock.yard.actions.closeFinding')}
              </Button>
            ) : null}
            {verifiable && can.return ? (
              <Button
                variant="default"
                color="red"
                leftSection={<IconArrowBack size={16} aria-hidden />}
                onClick={() => setForm({ verify: 'returned' })}
              >
                {t('drydock.yard.actions.return')}
              </Button>
            ) : null}
            {verifiable && can.verify ? (
              <Button
                leftSection={<IconCheck size={16} aria-hidden />}
                onClick={() => setForm({ verify: 'verified' })}
              >
                {t('drydock.yard.actions.verify')}
              </Button>
            ) : null}
          </Group>
        </div>
      </Panel>
      <Panel title={t('drydock.yard.surveys')}>
        <TargetLinks links={finding.surveys ?? []} />
        {ship && projectOpen && can.create && source.link && source.linkCandidates ? (
          <Group justify="flex-end">
            <Button
              variant="default"
              leftSection={<IconLink size={16} aria-hidden />}
              onClick={() => setForm('link')}
            >
              {t('drydock.yard.actions.link')}
            </Button>
          </Group>
        ) : null}
      </Panel>
      {form === 'close' ? (
        <CloseFindingModal
          finding={finding}
          source={source}
          onClose={() => setForm(null)}
          onSaved={saved}
        />
      ) : null}
      {form === 'reopen' ? (
        <NoteModal
          title={t('drydock.yard.reopenTitle', { no: finding.findingNo })}
          label={t('drydock.scope.fields.reason')}
          action={t('drydock.yard.actions.reopenFinding')}
          onClose={() => setForm(null)}
          onSave={(reason) =>
            (source.reopenFinding as NonNullable<DrydockScopeSource['reopenFinding']>)({
              findingId: finding.id,
              reason,
            })
          }
          onSaved={saved}
        />
      ) : null}
      {form !== null && typeof form === 'object' && closure ? (
        <VerificationModal
          title={t(`drydock.yard.verificationTitle.${form.verify}`, { no: finding.findingNo })}
          hint={t(`drydock.yard.findingVerificationHint.${form.verify}`)}
          verdict={form.verify}
          onClose={() => setForm(null)}
          onSave={(input) =>
            (source.verifyFinding as NonNullable<DrydockScopeSource['verifyFinding']>)({
              findingId: finding.id,
              verification: {
                verdict: form.verify,
                note: input.note,
                contentHash: closure.contentHash,
              },
              ...input.body,
            })
          }
          onSaved={saved}
        />
      ) : null}
      {form === 'link' ? (
        <LinkModal
          title={t('drydock.yard.linkTitle', { no: finding.findingNo })}
          types={['survey']}
          source={source}
          onClose={() => setForm(null)}
          onLink={(candidate) =>
            (source.link as NonNullable<DrydockScopeSource['link']>)({
              projectId: scope.project.id,
              link: {
                sourceType: 'finding',
                sourceId: finding.id,
                targetType: 'survey',
                targetId: candidate.id,
                relation: 'related',
              },
            })
          }
          onSaved={saved}
        />
      ) : null}
    </>
  );
}

/** Bulgu kapanışı (Screen 11; kapanış onayıdır, ayar açıksa parola). */
function CloseFindingModal({
  finding,
  source,
  onClose,
  onSaved,
}: {
  finding: PdiFindingRow;
  source: DrydockScopeSource;
  onClose: () => void;
  onSaved: (scope: DrydockScope) => void;
}) {
  const { t } = useTranslation();
  const reauth = useReauth('drydock_close');
  const [note, setNote] = useState('');
  const { busy, error, run } = useSave(
    () =>
      (source.closeFinding as NonNullable<DrydockScopeSource['closeFinding']>)({
        findingId: finding.id,
        note: note.trim(),
        ...reauth.body(),
      }),
    onSaved,
  );
  return (
    <Modal
      opened
      onClose={onClose}
      title={t('drydock.yard.closeFindingTitle', { no: finding.findingNo })}
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
    >
      <div className={classes.form}>
        <p className={classes.muted}>{t('drydock.yard.closeFindingHint')}</p>
        {error ? <Alert color="red">{error}</Alert> : null}
        <Textarea
          label={t('drydock.yard.note')}
          required
          autosize
          minRows={2}
          maxLength={2000}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
        />
        <ReauthField reauth={reauth} label={t('drydock.scope.password')} />
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose}>
            {t('drydock.cancel')}
          </Button>
          <Button
            loading={busy}
            disabled={reauth.missing || note.trim() === ''}
            onClick={() =>
              void run().then((err) => {
                if (err) reauth.noteError(err);
              })
            }
          >
            {t('drydock.yard.actions.closeFinding')}
          </Button>
        </Group>
      </div>
    </Modal>
  );
}

/** Gerekçe penceresi (yeniden açma). */
function NoteModal({
  title,
  label,
  action,
  onClose,
  onSave,
  onSaved,
}: {
  title: string;
  label: string;
  action: string;
  onClose: () => void;
  onSave: (note: string) => Promise<DrydockScope>;
  onSaved: (scope: DrydockScope) => void;
}) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const { busy, error, run } = useSave(() => onSave(note.trim()), onSaved);
  return (
    <Modal
      opened
      onClose={onClose}
      title={title}
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
    >
      <div className={classes.form}>
        {error ? <Alert color="red">{error}</Alert> : null}
        <Textarea
          label={label}
          required
          autosize
          minRows={2}
          maxLength={2000}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
        />
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose}>
            {t('drydock.cancel')}
          </Button>
          <Button loading={busy} disabled={note.trim() === ''} onClick={() => void run()}>
            {action}
          </Button>
        </Group>
      </div>
    </Modal>
  );
}
